import React from 'react';
import 'regenerator-runtime/runtime';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/browser';
import { isIOS, isSafari } from 'react-device-detect';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { ENVIRONMENT, SPLIT_CHANNEL, SENTRY_DSN, RECAPTCHA_KEY } from './config';
import { sendSubscription } from './utils/subscription';
import App from './App';
import store from './store';
import { createTheme, ThemeProvider } from '@mui/material';
// import registerServiceWorker from './utils/registerServiceWorker';

// Transient websocket errors that Sendbird UIKit leaves as unhandled promise rejections
// (CONNECTION_REQUIRED, NETWORK_ERROR, ACK_TIMEOUT, LOGIN_TIMEOUT, WEBSOCKET_CONNECTION_CLOSED/FAILED).
// The SDK reconnects on its own, so they are noise in Sentry.
const SENDBIRD_CONNECTION_ERROR_CODES = [800101, 800120, 800180, 800190, 800200, 800210];

const isSendbirdConnectionError = error => (
  error?.name === 'SendbirdError' && SENDBIRD_CONNECTION_ERROR_CODES.includes(error.code)
);

if (ENVIRONMENT !== 'development') {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release: process.env.REACT_APP_RELEASE_VERSION, // eslint-disable-line
    ignoreErrors: [
      'Non-Error exception captured',
      'Non-Error promise rejection captured'
    ],
    beforeSend: (event, hint) => (isSendbirdConnectionError(hint?.originalException) ? null : event),
    tracesSampleRate: 0.1
  });
}

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 700,
      md: 1000,
      lg: 1536,
      xl: 1920
    }
  }
});

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <GoogleReCaptchaProvider
    reCaptchaKey={RECAPTCHA_KEY}
    language="en"
    useRecaptchaNet
  >
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <Router>
          <App />
        </Router>
      </Provider>
    </ThemeProvider>
  </GoogleReCaptchaProvider>
);

if (!isIOS && !isSafari && window.BroadcastChannel) {
  const channel = new BroadcastChannel(SPLIT_CHANNEL);
  channel.addEventListener('message', event => {
    const data = event.data;
    sendSubscription(data);
    channel.close();
  });
}

// registerServiceWorker({
//   onUpdate: () => {
//     if (window.alert('New version of the app available! Would you like to update it now?')) {
//       window.location.reload();
//     }
//   }
// });
